/**
 * <s-size-chart-link>
 * Wraps a link to the size chart in the product description (#product-size-chart).
 * On click: opens the chart large in a pop-up dialog (merchant, 31 Oct 2026), with the selected size's
 * row highlighted. Closes with X, a tap on the backdrop or Escape. Without <dialog> support it falls
 * back to opening the description and scrolling to the chart. Hides itself if the chart is missing.
 *
 * Attributes: data-close-label, data-selected-label (translated strings from Liquid).
 */
class SizeChartLink extends HTMLElement {
  #ac = null;
  #dialog = null;

  connectedCallback() {
    const link = this.querySelector('a[href^="#"]');
    const target = link && document.getElementById(link.getAttribute('href').slice(1));
    if (!target) {
      this.hidden = true;
      return;
    }
    this.#ac = new AbortController();
    link.addEventListener('click', (event) => {
      event.preventDefault();
      if (typeof HTMLDialogElement === 'function') {
        this.#open(target, link);
      } else {
        this.#scrollTo(target);
      }
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#dialog?.remove();
  }

  #scrollTo(target) {
    let node = target;
    while (node) {
      if (node instanceof HTMLDetailsElement) node.open = true;
      node = node.parentElement?.closest('details') ?? null;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.querySelector('summary')?.focus({ preventScroll: true });
  }

  #selectedSize() {
    const checked = [...document.querySelectorAll('[data-product-form] s-variant-picker input:checked')];
    const size = checked.find((input) => /^(xxs|xs|s|m|l|xl|xxl|[2-5]xl)$/i.test(input.value.trim()));
    return size?.value.trim().toLowerCase() || '';
  }

  #open(target, link) {
    const table = target.querySelector('table');
    if (!table) {
      this.#scrollTo(target);
      return;
    }
    if (!this.#dialog) this.#dialog = this.#build(target, table);
    const dialog = this.#dialog;

    // Highlight the selected size each time the pop-up opens.
    const size = this.#selectedSize();
    let marked = false;
    dialog.querySelectorAll('tbody tr').forEach((row) => {
      const first = row.querySelector('td, th')?.textContent.trim().toLowerCase() || '';
      const match = Boolean(size) && first === size;
      row.toggleAttribute('data-selected', match);
      if (match) marked = true;
    });
    const hint = dialog.querySelector('[data-size-chart-hint]');
    if (hint) hint.hidden = !marked;

    dialog.showModal();
    dialog.addEventListener('close', () => link.focus({ preventScroll: true }), { once: true });
  }

  #build(target, table) {
    const dialog = document.createElement('dialog');
    dialog.className = 'ma-size-dialog';
    const heading = target.querySelector('summary h4')?.textContent.trim() || '';
    const note = target.querySelector('summary h4 + span')?.textContent.trim() || '';
    dialog.setAttribute('aria-label', heading);

    const head = document.createElement('div');
    head.className = 'ma-size-dialog-head';
    const titles = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'ma-size-dialog-title';
    title.textContent = heading;
    titles.append(title);
    if (note) {
      const sub = document.createElement('p');
      sub.className = 'ma-size-dialog-note';
      sub.textContent = note;
      titles.append(sub);
    }
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ma-size-dialog-close';
    close.setAttribute('aria-label', this.dataset.closeLabel || '');
    close.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    close.addEventListener('click', () => dialog.close());
    head.append(titles, close);

    const body = document.createElement('div');
    body.className = 'ma-size-dialog-body';
    body.append(table.cloneNode(true));

    const hint = document.createElement('p');
    hint.className = 'ma-size-dialog-hint';
    hint.dataset.sizeChartHint = '';
    hint.textContent = this.dataset.selectedLabel || '';

    dialog.append(head, body, hint);
    // A tap on the backdrop (the dialog element itself, outside its content) closes it.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    document.body.append(dialog);
    return dialog;
  }
}

if (!customElements.get('s-size-chart-link')) customElements.define('s-size-chart-link', SizeChartLink);
